import type { BannerStyle } from "./services/banners";

/**
 * Text readability over a banner image — what the editor's "Auto" text colour and "Auto" overlay
 * decide from, and the contrast figure the preview reports.
 *
 * Only the part of the image behind the text counts (`textRegion`), and it is judged by its worst
 * pixels rather than its average: light text is only as readable as the brightest tenth behind it,
 * dark text as the darkest tenth. Contrast is WCAG's ratio, aiming for 4.5:1 — the subtitle and
 * button are small text. The decisions are saved as plain values (`textTone`, `overlay`,
 * `overlayOpacity`), so the storefront draws exactly what the preview showed.
 */

export type RegionLight = {
  /** Relative luminance (0 black – 1 white) of the darkest tenth of the region's pixels. */
  low: number;
  /** …and of its brightest tenth. */
  high: number;
};

export const TARGET_CONTRAST = 4.5;

/** The storefront's two text colours: white, and `--title-color` (#202326). */
const LIGHT_TEXT = 1;
const DARK_TEXT = luminance(32, 35, 38);
/** The overlay colours: Steelyard navy (rgb 8, 22, 48) under light text, white under dark. */
const NAVY = luminance(8, 22, 48);
const WHITE = 1;
/** A gradient is full strength only at its edge; behind the text it averages about this much of it. */
const GRADIENT_SHARE = 0.75;

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4;
}

export function luminance(r: number, g: number, b: number): number {
  return 0.2126 * channel(r) + 0.7152 * channel(g) + 0.0722 * channel(b);
}

export function contrast(a: number, b: number): number {
  return (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
}

/** The 10th and 90th percentile luminance of an RGBA pixel buffer (`ImageData.data`). */
export function regionLight(rgba: Uint8ClampedArray): RegionLight | null {
  const values: number[] = [];
  for (let i = 0; i + 3 < rgba.length; i += 4) values.push(luminance(rgba[i]!, rgba[i + 1]!, rgba[i + 2]!));
  if (values.length === 0) return null;
  values.sort((x, y) => x - y);
  const at = (q: number) => values[Math.min(values.length - 1, Math.floor(q * values.length))]!;
  return { low: at(0.1), high: at(0.9) };
}

export type Tone = "dark" | "light";

/** The worst-case contrast of the text a tone carries — `textTone` names the ground, so "dark" carries light text. */
export function worstContrast(light: RegionLight, tone: Tone): number {
  return tone === "dark" ? contrast(LIGHT_TEXT, light.high) : contrast(DARK_TEXT, light.low);
}

/** The region's luminance once an overlay of this kind and opacity (percent) is drawn over it. */
function underOverlay(light: RegionLight, tone: Tone, overlay: BannerStyle["overlay"], opacity: number): RegionLight {
  if (overlay === "none" || opacity <= 0) return light;
  const a = (opacity / 100) * (overlay === "gradient" ? GRADIENT_SHARE : 1);
  const tint = tone === "dark" ? NAVY : WHITE;
  return { low: light.low * (1 - a) + tint * a, high: light.high * (1 - a) + tint * a };
}

export type Readability = {
  tone: Tone;
  overlay: BannerStyle["overlay"];
  overlayOpacity: number;
  /** The text's worst-case contrast with everything above applied. */
  ratio: number;
};

/**
 * The overlays "Auto" may choose, lightest first: a gradient keeps most of the photo, so every
 * gradient step comes before a solid tint; a tint stops at 60%, past which the photo is gone.
 */
const AUTO_OVERLAYS: readonly { overlay: BannerStyle["overlay"]; opacity: number }[] = [
  ...[10, 20, 30, 40, 50, 60, 70, 80].map((opacity) => ({ overlay: "gradient" as const, opacity })),
  ...[30, 40, 50, 60].map((opacity) => ({ overlay: "solid" as const, opacity })),
];

/**
 * What the banner should use: the given tone and overlay, with "auto" ones worked out — the tone
 * whose text contrasts more, and the lightest overlay that brings the text to 4.5:1 (or the
 * strongest allowed, where nothing does). An image that already reads at 4.5:1 gets no overlay.
 */
export function decideReadability(
  light: RegionLight,
  choice: { tone: Tone; autoTone: boolean; overlay: BannerStyle["overlay"]; overlayOpacity: number; autoOverlay: boolean },
): Readability {
  const tone: Tone = choice.autoTone
    ? worstContrast(light, "dark") >= worstContrast(light, "light") ? "dark" : "light"
    : choice.tone;

  let overlay = choice.overlay;
  let overlayOpacity = choice.overlayOpacity;
  if (choice.autoOverlay) {
    overlay = "none";
    overlayOpacity = 0;
    if (worstContrast(light, tone) < TARGET_CONTRAST) {
      const enough = AUTO_OVERLAYS.find((o) => worstContrast(underOverlay(light, tone, o.overlay, o.opacity), tone) >= TARGET_CONTRAST);
      ({ overlay, opacity: overlayOpacity } = enough ?? AUTO_OVERLAYS[AUTO_OVERLAYS.length - 1]!);
    }
  }

  return { tone, overlay, overlayOpacity, ratio: worstContrast(underOverlay(light, tone, overlay, overlayOpacity), tone) };
}

/** A contrast ratio as people read it: "4.6:1". */
export function formatRatio(ratio: number): string {
  return `${ratio.toFixed(1)}:1`;
}

/**
 * The part of the banner the text occupies, as fractions of its width and height — where its
 * position and padding put the block, widened a little, since a title can run long.
 */
export function textRegion(style: Pick<BannerStyle, "align" | "verticalAlign" | "padding">): { x: number; y: number; w: number; h: number } {
  const pad = { compact: [0.06, 0.08], normal: [0.08, 0.12], spacious: [0.1, 0.15] }[style.padding];
  const [px, py] = pad as [number, number];
  const [x0, x1] = style.align === "left" ? [px, 0.68] : style.align === "right" ? [0.32, 1 - px] : [0.18, 0.82];
  const [y0, y1] = style.verticalAlign === "top" ? [py, 0.55] : style.verticalAlign === "bottom" ? [0.45, 1 - py] : [0.25, 0.75];
  return { x: x0, y: y0, w: x1 - x0, h: y1 - y0 };
}

/**
 * Reads the text region of an image as the banner will crop it (cover, held at `focus`), on a small
 * canvas. Null where the browser cannot (no canvas, an image that failed to load) — the editor then
 * keeps the colour and overlay it has.
 */
export async function sampleTextRegion(
  src: string,
  aspect: number,
  style: Pick<BannerStyle, "align" | "verticalAlign" | "padding" | "imageFocus">,
): Promise<RegionLight | null> {
  try {
    // Load events, not `image.decode()`: decode() was seen to stay pending for good when called while
    // the editor was still loading (Chrome), leaving the contrast check silently never answering.
    const image = new Image();
    await new Promise<void>((resolve, reject) => {
      image.onload = () => resolve();
      image.onerror = () => reject(new Error(`could not load ${src}`));
      image.src = src;
    });
    const width = 96;
    const height = Math.max(8, Math.round(width / aspect));
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context || !image.naturalWidth) return null;

    // `object-fit: cover`, anchored the way the banner's `imageFocus` anchors it.
    const scale = Math.max(width / image.naturalWidth, height / image.naturalHeight);
    const drawn = { w: image.naturalWidth * scale, h: image.naturalHeight * scale };
    const dx = style.imageFocus === "left" ? 0 : style.imageFocus === "right" ? width - drawn.w : (width - drawn.w) / 2;
    context.drawImage(image, dx, (height - drawn.h) / 2, drawn.w, drawn.h);

    const r = textRegion(style);
    const data = context.getImageData(
      Math.floor(r.x * width),
      Math.floor(r.y * height),
      Math.max(1, Math.round(r.w * width)),
      Math.max(1, Math.round(r.h * height)),
    ).data;
    return regionLight(data);
  } catch {
    return null;
  }
}
